import { useState, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Swal from "sweetalert2";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayoutMin from "examples/LayoutContainers/DashboardLayoutMin";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Chip,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Dialog,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getBase64FromImageUrl } from "utils/imageBase64";
import logo from "assets/images/logos/logo.png";

pdfMake.vfs = pdfFonts.vfs;
const user = JSON.parse(sessionStorage.getItem("user"));
const clientId = user?.client_id;
const collegeId = user?.college_id;
const user_email = user?.user_email;
// Helper function to convert number to words
function numberToWords(amount) {
  let number = Math.floor(amount);
  if (number === 0) return "Zero";

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const scales = ["", "Thousand", "Lakh", "Crore"];

  function convertChunk(n) {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ones[n % 10] : "");
    return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convertChunk(n % 100) : "");
  }

  let result = "";
  let scaleIndex = 0;

  // Handle crores (lakhs * 100)
  if (number >= 10000000) {
    const crore = Math.floor(number / 10000000);
    result += convertChunk(crore) + " Crore ";
    number %= 10000000;
  }
  // Handle lakhs
  if (number >= 100000) {
    const lakh = Math.floor(number / 100000);
    result += convertChunk(lakh) + " Lakh ";
    number %= 100000;
  }
  // Handle thousands
  if (number >= 1000) {
    const thousand = Math.floor(number / 1000);
    result += convertChunk(thousand) + " Thousand ";
    number %= 1000;
  }
  // Handle remaining
  if (number > 0) {
    result += convertChunk(number);
  }

  return result.trim() + " Rupees";
}

const isTuitionFeeBook = (book) => {
  const feeTypeName = String(book?.feeHead?.feeType?.fee_type_name || "").toUpperCase();
  const feeHeadName = String(
    book?.feeHead?.fee_head_name || book?.fee_head_name || ""
  ).toUpperCase();
  return (
    feeTypeName.includes("TUITION") ||
    feeTypeName.includes("TUTION") ||
    feeHeadName.includes("TUITION") ||
    feeHeadName.includes("TUTION")
  );
};

// Fee receipt PDF generator using pdfmake, matching the provided sample

async function generateFeeReceiptPDF(receiptData) {
  try {
    const clientId = receiptData.client_id;
    const collegeId = receiptData.college_id;
    const receiptId = receiptData.fee_receipt_id;

    if (!clientId || !collegeId || !receiptId) {
      throw new Error("Receipt information is missing");
    }

    const url =
      `${API_BASE_URL}/api/fee-receipts/generate-receiptpdf/` +
      `${clientId}/${collegeId}/${receiptId}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/pdf",
      },
    });

    if (!response.ok) {
      let message = "Unable to generate fee receipt";

      try {
        const errorData = await response.json();
        message = errorData.message || message;
      } catch {
        // PDF/API returned non-JSON response
      }

      throw new Error(message);
    }

    const blob = await response.blob();

    const pdfUrl = window.URL.createObjectURL(blob);

    window.open(pdfUrl, "_blank");

    setTimeout(() => {
      window.URL.revokeObjectURL(pdfUrl);
    }, 60000);
  } catch (error) {
    console.error("PDF ERROR:", error);

    showSnackbar(error?.message || "Unable to generate fee receipt", "error");
  }
}

function FeeReceipt({ onBack }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [collegeId, setCollegeId] = useState(null);

  const [receipts, setReceipts] = useState([]);
  const [feeBooks, setFeeBooks] = useState([]);
  const [students, setStudents] = useState([]);

  const [freditingId, setFREditingId] = useState(null);

  // New state for fee collection modal
  const [searchRollNo, setSearchRollNo] = useState("");
  const [searchName, setSearchName] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [matchingStudents, setMatchingStudents] = useState([]);

  // Fetch college details
  const [collegeDetails, setCollegeDetails] = useState(null);

  useEffect(() => {
    const fetchCollegeDetails = async () => {
      if (!clientId || !collegeId) return;
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/colleges/college/${clientId}/${collegeId}`,
          { headers: getAuthHeaders() }
        );
        setCollegeDetails(data?.data || null);
      } catch (error) {
        setCollegeDetails(null);
      }
    };
    fetchCollegeDetails();
  }, [clientId, collegeId]);
  const [studentFeeBooks, setStudentFeeBooks] = useState([]);
  const [paymentMode, setPaymentMode] = useState("");
  const [selectedFeeType, setSelectedFeeType] = useState("");
  const [trnDate, setTrnDate] = useState(new Date().toISOString().split("T")[0]);
  const [noOfCopies, setNoOfCopies] = useState(1);

  // Fee Type dropdown state
  const [feeTypeOptions, setFeeTypeOptions] = useState([]);

  const [feeReceiptFormData, setFeeReceiptFormData] = useState({
    fee_book_id: "",
    student_id: "",
    student_rollno: "",
    receipt_date: "",
    fee_amount: "",
    payment_mode: "",
    trn_ref_no: "",
    trn_ref_date: "",
    remarks: "",
  });
  const [feeReceiptFormErrors, setFeeReceiptFormErrors] = useState({});

  // 🎯 NEW PAYMENT MODAL STATES (Step 1)
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [selectedFeeRow, setSelectedFeeRow] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    fee_amount: "",
    payment_mode: "cash",
  });
  const [justPaidSuccessfully, setJustPaidSuccessfully] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const isEditMode = Boolean(freditingId);

  /* ---------------- AUTH ---------------- */
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    const userData = sessionStorage.getItem("user");
    const token = sessionStorage.getItem("token");

    let extractedClientId = null;
    let extractedCollegeId = null;

    if (userData) {
      const parsedUser = JSON.parse(userData);

      setUser(parsedUser);

      // Extract client_id and college_id from user object
      // Support both camelCase and snake_case
      const extractedClientId = parsedUser.client_id;
      const extractedCollegeId = parsedUser.college_id;

      if (
        !Number.isInteger(Number(extractedClientId)) ||
        extractedClientId <= 0 ||
        !Number.isInteger(Number(extractedCollegeId)) ||
        extractedCollegeId <= 0
      ) {
        console.warn("User is missing client_id or college_id in session data");
        showSnackbar("1.User information incomplete. Please login again.", "error");
        setClientId(null);
        setCollegeId(null);
        return;
      }

      setClientId(extractedClientId);
      setCollegeId(extractedCollegeId);
    } else {
      console.warn("No user data found in session");
      showSnackbar("Please login to access this page.", "error");
    }
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleInvalidClientContext = () => {
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    showSnackbar("Session expired or invalid. Please sign in again.", "error");
    setTimeout(() => navigate("/authentication/sign-in"), 800);
  };

  /* ---------------- DATA FETCH ---------------- */
  useEffect(() => {
    if (clientId && collegeId) {
      // Initial load: fetch only dropdown categories.
      // Receipts / students / fee-books will be fetched only when user searches/selects roll-no/name.
      fetchFeeCategories();
    }
  }, [clientId, collegeId]);

  // Fetch fee categories for dropdown

  const fetchFeeCategories = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-categories/${clientId}/${collegeId}`,
        { headers: getAuthHeaders() }
      );
      setFeeTypeOptions(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      setFeeTypeOptions([]);
    }
  };

  const fetchReceipts = async (studentRollNo = "") => {
    if (!clientId || !collegeId) {
      showSnackbar("2.User information incomplete. Please login again.", "error");
      return;
    }
    try {
      const rollNo = String(studentRollNo || "").trim();
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}`,
        {
          headers: getAuthHeaders(),
          params: rollNo ? { studentRollno: rollNo } : {},
        }
      );
      const mappedReceipts = (data.data || []).map((receipt) => ({
        ...receipt,
        fee_book_name: receipt.feeBook?.feeHead?.fee_head_name || "N/A",
        student_name: receipt.student_rollno || "N/A",
        receipt_date: receipt.receipt_date
          ? new Date(receipt.receipt_date).toLocaleDateString()
          : "N/A",
        trn_ref_date: receipt.trn_ref_date
          ? new Date(receipt.trn_ref_date).toLocaleDateString()
          : "",
      }));
      setReceipts(mappedReceipts);
    } catch (error) {
      console.error("Error fetching receipts:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Error fetching receipts";
      if (errorMessage === "Invalid clientId") {
        handleInvalidClientContext();
        return;
      }
      showSnackbar(errorMessage, "error");
    }
  };

  const fetchFeeBooks = async () => {
    if (!clientId || !collegeId) {
      console.warn("Missing clientId or collegeId");
      showSnackbar("3.User information incomplete. Please login again.", "error");
      return;
    }
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}`, {
        headers: getAuthHeaders(),
      });
      const approvedBooks = (data.data || data.items || []).filter(
        (book) => book.workflow_status === "APPROVED"
      );
      setFeeBooks(approvedBooks);
    } catch (error) {
      console.error("Error fetching fee books:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Error fetching fee books";
      if (errorMessage === "Invalid clientId") {
        handleInvalidClientContext();
        return;
      }
      showSnackbar(errorMessage, "error");
    }
  };

  const fetchStudents = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/students/list/${clientId}/${collegeId}`,
        { headers: getAuthHeaders() }
      );
      const studentList = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setStudents(studentList);
      return studentList;
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.response?.data?.message || error.message || "Error fetching students";
      if (errorMessage === "Invalid clientId") {
        handleInvalidClientContext();
        return [];
      }
      showSnackbar("Error fetching students", "error");
      return [];
    }
  };

  /* ---------------- PRINT RECEIPT HANDLER ---------------- */
  // Download receipt PDF by receiptId
  const handleDownloadReceiptPdf = async (receiptId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/${receiptId}/generate-pdf`,
        {},
        { headers: getAuthHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Error opening receipt", "error");
    }
  };

  /* ---------------- SEARCH STUDENT ---------------- */
  const handleSearchByRollNo = async () => {
    if (!searchRollNo.trim()) {
      showSnackbar("Please enter a Roll Number", "warning");
      return;
    }
    try {
      const rollNo = searchRollNo.trim();
      setMatchingStudents([]);
      setSelectedStudent(null);
      setStudentFeeBooks([]);
      setReceipts([]);

      const { data } = await axios.get(
        `${API_BASE_URL}/api/students/rollno/${clientId}/${collegeId}/${encodeURIComponent(
          rollNo
        )}`,
        { headers: getAuthHeaders() }
      );
      const student = data?.student || data?.data || null;

      if (student) {
        setSelectedStudent(student);
        setSearchName(student.student_name || "");
        fetchReceipts(student.student_rollno);
        fetchStudentFeeBooks(student.student_id, student.student_rollno);
      } else {
        showSnackbar("Student Details are not found", "error");
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 404) {
        showSnackbar("Student Details are not found", "error");
        return;
      }
      showSnackbar("Error searching student", "error");
    }
  };

  const handleSearchByName = async (student = null) => {
    if (!student && !searchName.trim()) {
      showSnackbar("Please enter a Student Name", "warning");
      return;
    }
    try {
      const studentList = students.length > 0 ? students : await fetchStudents();

      if (student) {
        // Direct selection from autocomplete
        const fullStudent = await fetchStudentDetailsByRollNo(student.student_rollno, student);
        setSelectedStudent(fullStudent);
        setSearchName(fullStudent.student_name || "");
        setMatchingStudents([]);
        setReceipts([]);
        fetchReceipts(fullStudent.student_rollno);
        fetchStudentFeeBooks(fullStudent.student_id, fullStudent.student_rollno);
      } else {
        // Search and show all matches
        setSelectedStudent(null);
        setStudentFeeBooks([]);
        setReceipts([]);
        const matches = (studentList || []).filter(
          (s) =>
            s.student_name && s.student_name.toLowerCase().includes(searchName.trim().toLowerCase())
        );

        if (matches.length === 1) {
          // If only one match, select it directly
          const fullStudent = await fetchStudentDetailsByRollNo(
            matches[0].student_rollno,
            matches[0]
          );
          setSelectedStudent(fullStudent);
          setSearchName(fullStudent.student_name || "");
          setMatchingStudents([]);
          fetchReceipts(fullStudent.student_rollno);
          fetchStudentFeeBooks(fullStudent.student_id, fullStudent.student_rollno);
        } else if (matches.length > 1) {
          // If multiple matches, show them in table
          setMatchingStudents(matches);
          setSelectedStudent(null);
        } else {
          showSnackbar("Student Details are not found", "error");
          setMatchingStudents([]);
        }
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Error searching student", "error");
    }
  };

  const fetchStudentDetailsByRollNo = async (rollNo, fallbackStudent = null) => {
    const normalizedRollNo = String(rollNo || "").trim();
    if (!normalizedRollNo) return fallbackStudent;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/students/rollno/${clientId}/${collegeId}/${encodeURIComponent(
          normalizedRollNo
        )}`,
        { headers: getAuthHeaders() }
      );
      return data?.student || data?.data || fallbackStudent;
    } catch (error) {
      return fallbackStudent;
    }
  };

  const handleSelectStudentFromTable = async (student) => {
    const fullStudent = await fetchStudentDetailsByRollNo(student.student_rollno, student);
    setSelectedStudent(fullStudent);
    setSearchName(fullStudent.student_name || "");
    setMatchingStudents([]);
    setReceipts([]);
    // Convert student_id to number if it's a BigInt
    const studentIdNum =
      typeof fullStudent.student_id === "bigint"
        ? Number(fullStudent.student_id)
        : typeof fullStudent.student_id === "string"
        ? parseInt(fullStudent.student_id, 10)
        : fullStudent.student_id;
    fetchReceipts(fullStudent.student_rollno);
    fetchStudentFeeBooks(studentIdNum, fullStudent.student_rollno);
  };

  // 🎯 NEW: Open payment modal (Step 1)
  const handleOpenPayModal = (row) => {
    if (!selectedStudent) {
      showSnackbar("Please select a student first", "warning");
      return;
    }
    const maxDue = Number(row?.feeDueRaw || 0);
    if (maxDue <= 0) {
      showSnackbar("No pending due for this fee item", "info");
      return;
    }
    setSelectedFeeRow(row);
    setPaymentForm({
      fee_amount: maxDue,
      payment_mode: "cash",
    });
    setPayModalOpen(true);
  };

  // Helper function to safely convert BigInt to number for API calls
  const convertToNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "bigint") return Number(value);
    if (typeof value === "string") {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? value : parsed;
    }
    return value;
  };

  const fetchStudentFeeBooks = async (studentId, studentRollNo = "") => {
    try {
      // Use the specific endpoint for student fee books instead of fetching all and filtering
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/student/${studentId}`,
        { headers: getAuthHeaders() }
      );

      const allBooks = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.items)
        ? data.items
        : [];

      const normalizedRollNo = String(studentRollNo || "").trim();
      // Convert studentId to string for comparison - handle BigInt from server
      const normalizedStudentId = String(studentId);

      // Since we're using the student-specific endpoint, we don't need to filter by student_id
      // Just filter by workflow_status
      const studentBooks = allBooks.filter((book) => {
        // More flexible status check - handle null, undefined, or different cases
        const status = String(book.workflow_status || "")
          .toUpperCase()
          .trim();
        // Accept APPROVED, DRAFT, ACTIVE, or empty status
        const isVisibleStatus =
          status === "APPROVED" || status === "DRAFT" || status === "ACTIVE" || status === "";
        return isVisibleStatus;
      });

      const booksWithCalculations = await Promise.all(
        studentBooks.map(async (book) => {
          try {
            // Convert BigInt values to regular numbers for API calls
            const feeBookId =
              typeof book.fee_book_id === "bigint"
                ? Number(book.fee_book_id)
                : typeof book.fee_book_id === "string"
                ? parseInt(book.fee_book_id, 10)
                : book.fee_book_id;

            const studentIdNum =
              typeof book.student_id === "bigint"
                ? Number(book.student_id)
                : typeof book.student_id === "string"
                ? parseInt(book.student_id, 10)
                : book.student_id;

            const refundAmount = (book.feeRefunds || []).reduce(
              (sum, refund) => sum + parseFloat(refund.fee_amount || 0),
              0
            );
            const receiptTotalResponse = await axios.get(
              `${API_BASE_URL}/api/fee-receipts/${clientId}/fee-book/${feeBookId}/total`,
              {
                headers: getAuthHeaders(),
              }
            );
            const paidAmount = parseFloat(receiptTotalResponse?.data?.data?.totalAmount || 0);

            const concessionResponse = await axios.get(
              `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}/fee-book/${feeBookId}`,
              {
                headers: getAuthHeaders(),
                params: { workflowStatus: "APPROVED" },
              }
            );
            const concessionsData = concessionResponse.data.data || [];
            const discountAmount = concessionsData.reduce(
              (sum, c) => sum + parseFloat(c.fee_amount || 0),
              0
            );

            // Fee Due = Total Fee - Concession - Paid Amount + Refund
            const feeDue =
              parseFloat(book.fee_amount || 0) - discountAmount - paidAmount + refundAmount;
            const clampedFeeDue = Math.max(0, feeDue);

            return {
              ...book,
              paid: paidAmount,
              discount: discountAmount,
              refund: refundAmount,
              feeDue: clampedFeeDue,
              amountToPay: 0,
            };
          } catch (error) {
            return {
              ...book,
              paid: 0,
              discount: 0,
              feeDue: parseFloat(book.fee_amount || 0),
              amountToPay: 0,
            };
          }
        })
      );

      setJustPaidSuccessfully(false);
      setStudentFeeBooks(booksWithCalculations);
    } catch (error) {
      console.error(error);
      showSnackbar(error.response?.data?.message || "Error fetching student fee books", "error");
    }
  };

  const calculateTotalPending = () => {
    return studentFeeBooks.reduce((sum, book) => sum + (book.feeDue || 0), 0).toFixed(2);
  };

  const formatDisplayAmount = (value) => {
    const numberValue = Number(value || 0);
    return Number.isInteger(numberValue) ? String(numberValue) : numberValue.toFixed(2);
  };
  const getStudentBranch = (student) => {
    if (!student) return "";

    return student.branch?.branch_name || student.branch_name || student.branch || "";
  };
  const getStudentBatch = (student) => {
    if (!student) return "";

    return (
      student.admittedbatch?.batch_name ||
      student.admittedbatch?.name ||
      student.batch_name ||
      student.batch ||
      student.admitted_batch ||
      ""
    );
  };
  const getTextValue = (value, keys = []) => {
    if (value === null || value === undefined) return "";

    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }

    if (typeof value === "object") {
      for (const key of keys) {
        if (value[key] !== null && value[key] !== undefined) {
          return String(value[key]);
        }
      }
    }

    return "";
  };
  const [processingReceipt, setProcessingReceipt] = useState(false);
  const [receiptStatus, setReceiptStatus] = useState("");
  const handleSingleRowPay = async () => {
    if (!selectedFeeRow) {
      showSnackbar("No fee item selected", "warning");
      return;
    }

    if (!selectedStudent) {
      showSnackbar("Please select a student first", "warning");
      return;
    }

    const row = selectedFeeRow;
    const maxDue = Number(row.feeDueRaw || 0);

    if (maxDue <= 0) {
      showSnackbar("No pending due for this fee item", "info");
      return;
    }

    const paidAmount = Number(paymentForm.fee_amount);

    if (!Number.isFinite(paidAmount) || paidAmount <= 0) {
      showSnackbar("Enter a valid fee amount", "warning");
      return;
    }

    if (paidAmount > maxDue) {
      showSnackbar(`Fee amount cannot exceed ₹${formatDisplayAmount(maxDue)}`, "warning");
      return;
    }

    if (!paymentForm.payment_mode) {
      showSnackbar("Select a payment mode", "warning");
      return;
    }

    // Open blank tab immediately.
    // This avoids browser popup blocking after await/fetch.
    let pdfWindow = null;

    try {
      //pdfWindow = window.open("", "_blank");
      setProcessingReceipt(true);
      const userEmail = user?.user_email || "unknown";

      const feeBookIdNum =
        typeof row.feeBookId === "bigint"
          ? Number(row.feeBookId)
          : typeof row.feeBookId === "string"
          ? parseInt(row.feeBookId, 10)
          : row.feeBookId;

      const studentIdNum =
        typeof selectedStudent.student_id === "bigint"
          ? Number(selectedStudent.student_id)
          : typeof selectedStudent.student_id === "string"
          ? parseInt(selectedStudent.student_id, 10)
          : selectedStudent.student_id;

      const dataToSend = {
        fee_book_id: feeBookIdNum,
        student_id: studentIdNum,
        student_rollno: selectedStudent.student_rollno,
        receipt_date: trnDate,
        fee_amount: paidAmount,
        payment_mode: paymentForm.payment_mode.toUpperCase(),
        trn_ref_no: "",
        trn_ref_date: trnDate,
        remarks: "",
        college_id: Number(collegeId),
        created_by: userEmail,
        academic_year: row.academicYear || "",
        fee_type:
          row.feeTypeName &&
          (row.feeTypeName.toLowerCase().includes("tuition") ||
            row.feeTypeName.toLowerCase().includes("tution"))
            ? "TUITION"
            : "",
      };

      // --------------------------------------------------
      // 1. CREATE RECEIPT
      // --------------------------------------------------

      const response = await axios.post(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}`,
        dataToSend,
        {
          headers: getAuthHeaders(),
        }
      );

      const receiptId = response?.data?.data?.fee_receipt_id;

      if (!receiptId) {
        throw new Error("Receipt was created but receipt ID was not returned");
      }

      // --------------------------------------------------
      // 2. GET RECEIPT DETAILS
      // --------------------------------------------------

      const receiptDetailResponse = await axios.get(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/${receiptId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      const receiptDetail = receiptDetailResponse?.data?.data;

      if (!receiptDetail) {
        throw new Error("Unable to get receipt details");
      }

      // --------------------------------------------------
      // 3. GENERATE SERVER-SIDE PDF
      // --------------------------------------------------
      // IMPORTANT:
      // generateFeeReceiptPDF() should call:
      //
      // POST /api/fee-receipts/:clientId/:collegeId/:receiptId/generate-pdf
      //
      // and return/use the PDF blob.
      //
      // Pass the receipt ID so server generates the same PDF
      // that is used for email.
      // --------------------------------------------------
      setReceiptStatus("Processing receipt PDF...");
      const pdfResponse = await axios.post(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/${receiptId}/generate-pdf`,
        {},
        {
          headers: {
            ...getAuthHeaders(),
            Accept: "application/pdf",
          },
          responseType: "blob",
        }
      );

      // --------------------------------------------------
      // 4. OPEN PDF IN ALREADY OPEN TAB
      // --------------------------------------------------

      const pdfBlob = new Blob([pdfResponse.data], {
        type: "application/pdf",
      });

      const pdfUrl = window.URL.createObjectURL(pdfBlob);

      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.location.href = pdfUrl;
      } else {
        // Fallback if browser blocked the first window
        window.open(pdfUrl, "_blank");
      }

      // Release object URL later
      setTimeout(() => {
        window.URL.revokeObjectURL(pdfUrl);
      }, 60000);

      // --------------------------------------------------
      // 5. UI UPDATE
      // --------------------------------------------------
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setReceiptStatus("Sending email...");
      setSearchRollNo("");

      setJustPaidSuccessfully(true);
      setPayModalOpen(false);
      showSnackbar("Payment successful. Receipt generated and email sent.", "success");
      // --------------------------------------------------
      // 6. REFRESH RECEIPTS / FEE BOOKS
      // --------------------------------------------------

      await fetchReceipts(selectedStudent.student_rollno);

      await fetchStudentFeeBooks(selectedStudent.student_id, selectedStudent.student_rollno);
    } catch (error) {
      console.error("Payment / PDF Error:", error);

      // Close blank PDF tab if something failed
      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.close();
      }

      let message = "Error processing payment";

      if (error?.response?.data?.message) {
        message = error.response.data.message;
      }

      showSnackbar(message, "error");
    } finally {
      setProcessingReceipt(false);
      setReceiptStatus("");
    }
  };

  /* ---------------- FORM HANDLERS ---------------- */
  const handleFeeReceiptChange = (name, value) => {
    setFeeReceiptFormData({ ...feeReceiptFormData, [name]: value });
    setFeeReceiptFormErrors({ ...feeReceiptFormErrors, [name]: "" });
  };

  const handleFeeReceiptSave = async () => {
    const errors = {};
    if (!feeReceiptFormData.fee_book_id) {
      errors.fee_book_id = "Fee Book is required";
    }
    if (!feeReceiptFormData.student_id) {
      errors.student_id = "Student is required";
    }
    if (!feeReceiptFormData.fee_amount || feeReceiptFormData.fee_amount <= 0) {
      errors.fee_amount = "Amount must be greater than 0";
    }
    if (!feeReceiptFormData.payment_mode) {
      errors.payment_mode = "Payment Mode is required";
    }
    setFeeReceiptFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const userEmail = user?.user_email || "unknown";
    try {
      let response;
      const dataToSend = {
        fee_book_id: Number(feeReceiptFormData.fee_book_id),
        student_id: Number(feeReceiptFormData.student_id),
        student_rollno: feeReceiptFormData.student_rollno || "",
        receipt_date: feeReceiptFormData.receipt_date || null,
        fee_amount: Number(feeReceiptFormData.fee_amount),
        payment_mode: feeReceiptFormData.payment_mode,
        trn_ref_no: feeReceiptFormData.trn_ref_no || "",
        trn_ref_date: feeReceiptFormData.trn_ref_date || null,
        remarks: feeReceiptFormData.remarks || "",
        college_id: Number(collegeId),
      };

      if (freditingId) {
        dataToSend.modified_by = userEmail;
        response = await axios.put(
          `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/${freditingId}`,
          dataToSend,
          { headers: getAuthHeaders() }
        );
      } else {
        dataToSend.created_by = userEmail;
        response = await axios.post(
          `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}`,
          dataToSend,
          { headers: getAuthHeaders() }
        );
      }

      if (response.data.success) {
        showSnackbar(response.data.message || "Fee Receipt saved successfully", "success");
        if (feeReceiptFormData.student_rollno) {
          fetchReceipts(feeReceiptFormData.student_rollno);
        } else {
          setReceipts([]);
        }
      } else {
        showSnackbar("Something went wrong. Please try again.", "error");
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Error saving Fee Receipt", "error");
    }
  };

  const handleUploadFeeReceipts = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("created_by", user?.user_email || user_email || "");

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/upload/csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const failedText = data.failed ? `, ${data.failed} failed` : "";
      showSnackbar(
        `${data.message || "Fee receipts uploaded successfully"}${failedText}`,
        "success"
      );
      fetchReceipts();
      if (selectedStudent?.student_id) {
        fetchStudentFeeBooks(selectedStudent.student_id, selectedStudent.student_rollno);
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Fee receipt upload failed", "error");
    } finally {
      event.target.value = "";
    }
  };

  const handleFeeReceiptEdit = (row) => {
    setFeeReceiptFormData({
      fee_book_id: row.fee_book_id || "",
      student_id: row.student_id || "",
      student_rollno: row.student_rollno || "",
      receipt_date: row.receipt_date ? row.receipt_date.split("T")[0] : "",
      fee_amount: row.fee_amount || "",
      payment_mode: row.payment_mode || "",
      trn_ref_no: row.trn_ref_no || "",
      trn_ref_date: row.trn_ref_date ? row.trn_ref_date.split("T")[0] : "",
      remarks: row.remarks || "",
    });
    setFREditingId(row.fee_receipt_id);
  };

  const handleFeeReceiptDelete = async (fee_receipt_id) => {
    const confirm = await Swal.fire({
      title: "Sure to delete Fee Receipt?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/${fee_receipt_id}`,
          { headers: getAuthHeaders() }
        );
        if (response.data.success) {
          showSnackbar("Fee Receipt deleted successfully", "success");
          if (selectedStudent?.student_rollno) {
            fetchReceipts(selectedStudent.student_rollno);
          } else {
            setReceipts([]);
          }
        }
      } catch (error) {
        showSnackbar(error.response?.data?.message || "Error deleting Fee Receipt", "error");
      }
    }
  };

  const unifiedLabelSx = {
    display: "block",
    mb: 0.6,
    fontWeight: 700,
    fontSize: "0.82rem",
    lineHeight: 1.2,
    color: "#1f2937",
  };

  const searchLabelSx = {
    ...unifiedLabelSx,
    minWidth: 112,
    mb: 0,
  };

  const feeHeaderCellSx = {
    color: "#fff !important",
    fontWeight: 700,
    border: "1px solid #6470be",
    "& .MuiTypography-root": {
      color: "#fff !important",
    },
  };

  const paymentInputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#fff",
      height: 36,
    },
  };

  const studentStripLabelSx = {
    color: "#d7e6fb",
    display: "block",
    mb: 0.4,
    fontSize: "0.78rem",
    fontWeight: 500,
    letterSpacing: 0.2,
  };

  const studentStripValueSx = {
    color: "#fff",
    fontWeight: 700,
    fontSize: "1.02rem",
    lineHeight: 1.25,
  };

  const neutralGridSx = {
    border: "1px solid #d9e1ec",
    "& .MuiDataGrid-columnHeaders": {
      bgcolor: "#e9edf3",
      color: "#344767",
      fontWeight: 700,
      borderBottom: "1px solid #d9e1ec",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      color: "#344767",
      fontWeight: 700,
    },
    "& .MuiDataGrid-iconButtonContainer": {
      display: "none",
    },
    "& .MuiDataGrid-sortIcon": {
      color: "#344767",
    },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #eef2f7",
    },
  };

  const matchingStudentRows = matchingStudents.map((student, index) => ({
    id: student.student_id,
    sno: index + 1,
    rollNumber: student.student_rollno || "N/A",
    studentName: student.student_name || "N/A",
    regulation: student.regulation || "N/A",
    status: (student.student_status || "ACTIVE").toUpperCase(),
    studentRecord: student,
  }));

  const matchingStudentColumns = [
    { field: "sno", headerName: "S.No", width: 90 },
    { field: "rollNumber", headerName: "Roll Number", minWidth: 170, flex: 1 },
    { field: "studentName", headerName: "Student Name", minWidth: 220, flex: 1.2 },
    { field: "regulation", headerName: "Regulation", minWidth: 140, flex: 0.8 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="medium"
          color={params.value === "ACTIVE" ? "success" : "default"}
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="medium"
          onClick={() => handleSelectStudentFromTable(params.row.studentRecord)}
          sx={{ bgcolor: "#1976d2", color: "#fff", "&:hover": { bgcolor: "#115293" } }}
        >
          View
        </Button>
      ),
    },
  ];

  const feeDetailRows = studentFeeBooks.map((book, index) => ({
    id: book.fee_book_id,
    sno: index + 1,
    feeBatchName: book.feeHead?.fee_head_name || book.fee_head_name || "N/A",
    totalFee: parseFloat(book.fee_amount || 0).toFixed(2),
    discount: formatDisplayAmount(book.discount || 0),
    refund: formatDisplayAmount(book.refund || book.refund_amount || 0),
    paid: formatDisplayAmount(book.paid || 0),
    feeDue: formatDisplayAmount(book.feeDue || 0),
    feeDueRaw: Math.max(0, Number(book.feeDue) || 0), // Always positive
    feeBookId: book.fee_book_id,
    academicYear: book.feeHead?.academic_year || "",
    feeTypeName: book.feeHead?.feeType?.fee_type_name || "",
    remarks: book.remarks,
  }));

  const feeDetailColumns = [
    { field: "sno", headerName: "S.No", width: 85 },
    {
      field: "feeDetails",
      headerName: "Fee Head / Remarks",
      minWidth: 370,
      flex: 3,
      renderCell: (params) => {
        const { feeBatchName, remarks } = params.row;

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center", // ✅ vertical center
              height: "100%", // ✅ fill cell height
              width: "100%",
            }}
          >
            {/* Fee Batch Name */}
            <div style={{ fontWeight: 600 }}>{feeBatchName}</div>

            {/* Remarks */}
            <div
              style={{
                fontSize: 13,
                color: "#555",
                fontWeight: 600,
                marginTop: 4,
                whiteSpace: "normal",
                wordBreak: "break-word", // ✅ break long strings
              }}
            >
              {remarks}
            </div>
          </div>
        );
      },
    },

    { field: "totalFee", headerName: "Total Fee", minWidth: 80, flex: 0.7 },
    { field: "discount", headerName: "Concession", minWidth: 85, flex: 0.7 },
    { field: "refund", headerName: "Refund", minWidth: 105, flex: 0.7 },
    { field: "paid", headerName: "Paid", minWidth: 95, flex: 0.7 },
    {
      field: "feeDue",
      headerName: "Fee Due",
      minWidth: 130,
      flex: 0.9,
      renderCell: (params) => (
        <TextField
          value={params.value}
          size="medium"
          disabled
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-input": {
              color: "#ff1f1f",
              fontWeight: 900,
              fontSize: 18,
              py: 0.9,
              textAlign: "left",
            },
            "& .MuiOutlinedInput-root": { bgcolor: "#efefef" },
          }}
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 100,
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="error"
          size="medium"
          sx={{
            minWidth: 64,
            fontWeight: 700,
            boxShadow: "none",
            bgcolor: "#1976d2",
            color: "#fff",
            "&:hover": { bgcolor: "#115293" },
          }}
          onClick={() => handleOpenPayModal(params.row)}
          disabled={params.row.feeDueRaw <= 0}
        >
          Pay
        </Button>
      ),
    },
  ];

  // New function to fetch receipts for printing - fetches from API to get actual receipt data
  const handlePrintDetailedReceipt = async () => {
    if (!selectedStudent || studentFeeBooks.length === 0) {
      showSnackbar("No student or fee records selected", "warning");
      return;
    }

    try {
      // Fetch all receipts for this student
      const studentIdNum =
        typeof selectedStudent.student_id === "bigint"
          ? Number(selectedStudent.student_id)
          : typeof selectedStudent.student_id === "string"
          ? parseInt(selectedStudent.student_id, 10)
          : selectedStudent.student_id;

      const response = await axios.get(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/student/${studentIdNum}/receipts`,
        { headers: getAuthHeaders() }
      );

      const receipts = response?.data?.data || [];

      if (receipts.length === 0) {
        showSnackbar("No receipts found for this student", "info");
        return;
      }

      // Helper to safely extract string values
      const getString = (val) => {
        if (typeof val === "string") return val;
        if (typeof val === "number") return String(val);
        if (val && typeof val === "object") {
          return val.fee_head_name || val.feeBatchName || val.name || "";
        }
        return "";
      };

      // Build a table of all receipts for the student
      const receiptTableBody = [
        [
          { text: "Receipt No.", bold: true, fontSize: 10 },
          { text: "Date", bold: true, fontSize: 10 },
          { text: "Fee Batch", bold: true, fontSize: 10 },
          { text: "Pay Mode", bold: true, fontSize: 10 },
          { text: "Amount", bold: true, fontSize: 10 },
        ],
      ];
      let totalAmount = 0;
      receipts.forEach((receipt) => {
        // Use fee head name directly from the receipt's feeBook (which has feeHead data attached from API)
        const feeBatch = getString(
          receipt.feeBook?.feeHead?.fee_head_name || receipt.feeBook?.fee_head_name || ""
        );
        const date = receipt.receipt_date
          ? new Date(receipt.receipt_date).toISOString().split("T")[0]
          : "";
        const amount = Number(receipt.fee_amount) || 0;
        totalAmount += amount;
        receiptTableBody.push([
          getString(receipt.receipt_no || receipt.fee_receipt_id),
          date,
          feeBatch,
          getString(receipt.payment_mode),
          amount,
        ]);
      });

      // Build receiptData for the student summary (header info)
      const firstReceipt = receipts[0] || {};
      // Use fee head name and academic year directly from the receipt's feeBook (which has feeHead data attached from API)
      const firstFeeBatch = getString(
        firstReceipt.feeBook?.feeHead?.fee_head_name || firstReceipt.feeBook?.fee_head_name || ""
      );
      const firstReceiptNo = getString(firstReceipt.receipt_no || firstReceipt.fee_receipt_id);
      // Get academic year from feeHead - this is the correct path
      const firstAcademicYear = firstReceipt?.feeBook?.feeHead?.academic_year || "";
      const receiptData = {
        logoBase64: null,
        dateTime: new Date().toLocaleString("en-GB"),
        institutionName: getString(collegeDetails?.college_name),
        institutionSubtitle: getString(collegeDetails?.affiliation),
        studentId: getString(selectedStudent?.student_id || selectedStudent?.studentId),
        studentName: getString(selectedStudent?.student_name || selectedStudent?.studentName),
        rollNo: getString(selectedStudent?.student_rollno),
        phone: getString(selectedStudent?.phone || selectedStudent?.mobile_no),
        course: getString(selectedStudent?.course || selectedStudent?.course_name),
        branch:
          getStudentBranch(selectedStudent) ||
          getTextValue(firstReceipt?.student?.branch, ["branch_name", "name"]) ||
          getTextValue(firstReceipt?.feeBook?.branch, ["branch_name", "name"]) ||
          getTextValue(firstReceipt?.feeBook?.branch_name) ||
          getTextValue(firstReceipt?.feeBook?.feeHead?.branch, ["branch_name", "name"]) ||
          getTextValue(firstReceipt?.feeBook?.feeHead?.branch_name),
        batch:
          getStudentBatch(selectedStudent) ||
          getTextValue(firstReceipt?.student?.admittedbatch, [
            "admittedbatch",
            "batch_name",
            "name",
          ]) ||
          getTextValue(firstReceipt?.feeBook?.batch_name) ||
          getTextValue(firstReceipt?.feeBook?.batch) ||
          getTextValue(firstReceipt?.feeBook?.feeHead?.batch_name) ||
          getTextValue(firstReceipt?.feeBook?.feeHead?.batch),
        collegeCode: getString(selectedStudent?.college_code || selectedStudent?.collegeCode),
        collegeLocation: getString(
          collegeDetails?.college_location || collegeDetails?.collegeLocation
        ),
        academicYear: firstAcademicYear ? getString(firstAcademicYear) : "",
        feeBatch: firstFeeBatch,
        receiptNo: firstReceiptNo,
      };

      // Generate PDF with all receipt details in a table
      let logoBase64 = receiptData.logoBase64;
      if (!logoBase64) {
        logoBase64 = await getBase64FromImageUrl(logo);
      }
      const buildDetailedReceiptCopy = (copyLabel) => [
        {
          text: copyLabel,
          bold: true,
          alignment: "right",
          fontSize: 9,
          margin: [0, 0, 0, 4],
        },
        {
          columns: [
            logoBase64 && typeof logoBase64 === "string" && logoBase64.startsWith("data:image")
              ? { image: logoBase64, width: 60, alignment: "left", margin: [0, 0, 10, 0] }
              : {},
            {
              stack: [
                {
                  text: receiptData.institutionName || "College Name",
                  fontSize: 10,
                  bold: true,
                  alignment: "center",
                  margin: [0, 0, 0, 2],
                },
                {
                  text: receiptData.institutionSubtitle || "",
                  fontSize: 9,
                  alignment: "center",
                  margin: [0, 0, 0, 4],
                },
                {
                  text: "Fee Receipt",
                  fontSize: 7,
                  bold: true,
                  alignment: "center",
                  margin: [0, 0, 0, 8],
                },
              ],
              alignment: "center",
              width: "*",
            },
            {
              text: "Date: " + new Date().toLocaleDateString("en-GB"),
              alignment: "right",
              fontSize: 10,
              width: 80,
            },
          ],
          columnGap: 10,
          margin: [0, 0, 0, 10],
        },
        {
          table: {
            widths: ["*", "*"],
            body: [
              [
                {
                  text: "Date: " + new Date().toLocaleDateString("en-GB"),
                  alignment: "left",
                  fontSize: 10,
                },
                {
                  text: "Student Id: " + (receiptData.studentId || ""),
                  alignment: "right",
                  fontSize: 10,
                },
              ],
              [
                {
                  text: "Name: " + (receiptData.studentName || ""),
                  alignment: "left",
                  fontSize: 10,
                },
                {
                  text: "Phone No.: " + (receiptData.phone || ""),
                  alignment: "right",
                  fontSize: 10,
                },
              ],
              [
                {
                  text: "Roll No: " + (receiptData.rollNo || ""),
                  alignment: "left",
                  fontSize: 10,
                },
                { text: "Batch: " + (receiptData.batch || ""), alignment: "right", fontSize: 10 },
              ],
              [
                {
                  text: "Branch: " + (receiptData.branch || ""),
                  alignment: "left",
                  fontSize: 10,
                },
                {
                  text: "Academic Year: " + (receiptData.academicYear || ""),
                  alignment: "right",
                  fontSize: 10,
                },
              ],
              [
                {
                  text: "College Code: " + (receiptData.collegeCode || ""),
                  alignment: "left",
                  fontSize: 10,
                },
                { text: "", alignment: "right", fontSize: 10 },
              ],
              [
                {
                  text: "Fee Batch: " + (receiptData.feeBatch || ""),
                  alignment: "left",
                  fontSize: 10,
                },
                {
                  text: "Receipt No.: " + (receiptData.receiptNo || ""),
                  alignment: "right",
                  fontSize: 10,
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
          },
          margin: [0, 0, 0, 0],
        },
        {
          table: {
            headerRows: 1,
            widths: ["auto", "auto", "*", "auto", "auto"],
            body: receiptTableBody,
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
          },
          margin: [0, 0, 0, 0],
        },
        {
          table: {
            widths: ["*"],
            body: [
              [
                {
                  text:
                    "Total Amount: " + totalAmount + " /-\nIn Words: " + numberToWords(totalAmount),
                  fontSize: 10,
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 0;
            },
            vLineWidth: function () {
              return 0;
            },
          },
          margin: [0, 0, 0, 0],
        },
        {
          text: "This is computer generated receipt, no signature is required",
          fontSize: 8,
          margin: [0, 2, 0, 0],
        },
      ];

      const docDefinition = {
        content: [
          ...buildDetailedReceiptCopy("ORIGINAL COPY"),
          {
            canvas: [{ type: "line", x1: 0, y1: 8, x2: 555, y2: 8, lineWidth: 0.5 }],
            margin: [0, 4, 0, 10],
          },
          ...buildDetailedReceiptCopy("DUPLICATE COPY"),
        ],
        defaultStyle: {
          fontSize: 9,
        },
        pageMargins: [20, 16, 20, 16],
      };
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.getBlob((blob) => {
        const pdfUrl = URL.createObjectURL(blob);

        if (pdfWindow && !pdfWindow.closed) {
          pdfWindow.location.href = pdfUrl;
        } else {
          window.open(pdfUrl, "_blank");
        }

        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 60000);
      });
    } catch (error) {
      console.error("Error fetching receipts for printing:", error);
      showSnackbar(
        error.response?.data?.message || "Error fetching receipts for printing",
        "error"
      );
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <DashboardLayoutMin>
      <MDBox pt={1} pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 1, overflow: "hidden" }}>
              <Box sx={{ p: 1.6, bgcolor: "#f4f4f4" }}>
                <Box
                  sx={{
                    border: "1px solid #d3d3d3",
                    borderRadius: 0,
                    overflow: "hidden",
                    bgcolor: "#f7f7f7",
                  }}
                >
                  {/*  {!selectedStudent && (   */}
                  <Box
                    sx={{
                      p: 1.8,
                      bgcolor: "#f7f7f7",
                      borderBottom: matchingStudents.length > 0 ? "1px solid #d5d5d5" : "none",
                    }}
                  >
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={5.5}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: { xs: "stretch", md: "center" },
                            gap: 1,
                            flexDirection: { xs: "column", md: "row" },
                          }}
                        >
                          <MDTypography
                            variant="button"
                            sx={{
                              ...searchLabelSx,
                              minWidth: { md: 105 },
                              whiteSpace: { md: "nowrap" },
                              fontSize: { xs: "14px", md: "14px" },
                            }}
                          >
                            Roll Number:
                          </MDTypography>

                          <TextField
                            placeholder="Search Roll Number"
                            value={searchRollNo}
                            onChange={(e) => setSearchRollNo(e.target.value)}
                            size="medium"
                            fullWidth
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleSearchByRollNo();
                              }
                            }}
                            sx={{
                              flex: 1,

                              "& .MuiOutlinedInput-root": {
                                bgcolor: "#fff",
                                height: { xs: 44, md: 40 },
                              },

                              "& .MuiInputBase-input": {
                                fontSize: { xs: "16px", md: "18px" },
                                padding: { xs: "8px 12px", md: "8px 12px" },
                              },
                            }}
                          />

                          <Button
                            variant="contained"
                            onClick={handleSearchByRollNo}
                            fullWidth
                            sx={{
                              minWidth: { xs: "100%", md: 70 },
                              width: { xs: "100%", md: "auto" },
                              px: 1,
                              height: { xs: 44, md: 36 },
                              boxShadow: "none",
                              bgcolor: "#1976d2",
                              color: "#fff",
                              "&:hover": {
                                bgcolor: "#115293",
                              },
                              fontSize: "0.8rem",
                            }}
                          >
                            Find
                          </Button>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6.5}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: { xs: "stretch", md: "center" },
                            gap: 1,
                            flexDirection: { xs: "column", md: "row" },
                          }}
                        >
                          <MDTypography
                            variant="button"
                            sx={{
                              ...searchLabelSx,
                              minWidth: { md: 90 },
                              whiteSpace: { md: "nowrap" },
                              fontSize: { xs: "14px", md: "14px" },
                            }}
                          >
                            Enter Name:
                          </MDTypography>

                          <TextField
                            placeholder="Enter The Name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            size="medium"
                            fullWidth
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleSearchByName();
                              }
                            }}
                            sx={{
                              flex: 1,

                              "& .MuiOutlinedInput-root": {
                                bgcolor: "#fff",
                                height: { xs: 44, md: 40 },
                              },

                              "& .MuiInputBase-input": {
                                fontSize: { xs: "16px", md: "20px" },
                                padding: { xs: "8px 12px", md: "8px 12px" },
                              },
                            }}
                          />

                          <Button
                            variant="contained"
                            onClick={handleSearchByName}
                            fullWidth
                            sx={{
                              minWidth: { xs: "100%", md: 70 },
                              width: { xs: "100%", md: "auto" },
                              px: 1,
                              height: { xs: 44, md: 36 },
                              boxShadow: "none",
                              bgcolor: "#1976d2",
                              color: "#fff",
                              "&:hover": {
                                bgcolor: "#115293",
                              },
                              fontSize: "0.8rem",
                            }}
                          >
                            Find
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  {/*  )} */}

                  {/* Matching Students Table */}
                  {matchingStudents.length > 0 && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Card>
                        <Box sx={{ bgcolor: "#1976d2", color: "#fff", p: 1.5 }}>
                          <MDTypography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "#fff !important" }}
                          >
                            Search Results - Select a Student
                          </MDTypography>
                        </Box>
                        <Box sx={{ width: "100%", bgcolor: "#fff" }}>
                          <DataGrid
                            autoHeight
                            rows={matchingStudentRows}
                            columns={matchingStudentColumns}
                            disableRowSelectionOnClick
                            pageSizeOptions={[5, 10, 25]}
                            initialState={{
                              pagination: { paginationModel: { pageSize: 5, page: 0 } },
                            }}
                            sx={neutralGridSx}
                          />
                        </Box>
                      </Card>
                    </Box>
                  )}

                  {selectedStudent && (
                    <>
                      <Box
                        sx={{
                          bgcolor: "#1976d2",
                          color: "#fff",
                          py: 1.8,
                          px: 2.2,
                          mb: 2,
                          borderRadius: 1,
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={2}>
                            <MDTypography variant="caption" sx={studentStripLabelSx}>
                              Roll Number
                            </MDTypography>
                            <MDTypography variant="h6" sx={studentStripValueSx}>
                              {selectedStudent.student_rollno}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDTypography variant="caption" sx={studentStripLabelSx}>
                              Student Name
                            </MDTypography>
                            <MDTypography variant="h6" sx={studentStripValueSx}>
                              {(selectedStudent.student_name || "").toUpperCase()}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDTypography variant="caption" sx={studentStripLabelSx}>
                              Branch
                            </MDTypography>
                            <MDTypography variant="h6" sx={studentStripValueSx}>
                              {getStudentBranch(selectedStudent).toUpperCase()}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <MDTypography variant="caption" sx={studentStripLabelSx}>
                              Status
                            </MDTypography>
                            <Chip
                              label={(selectedStudent.student_status || "ACTIVE").toUpperCase()}
                              size="medium"
                              sx={{ bgcolor: "#4caf50", color: "#fff", fontWeight: 700 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDTypography variant="caption" sx={studentStripLabelSx}>
                              Contact Details
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              sx={{ color: "#fff", fontSize: "0.95rem", lineHeight: 1.45 }}
                            >
                              Caste: {selectedStudent.caste || "N/A"}
                              <br />
                              Seat: {selectedStudent.seat_type || "C"} | Phone:{" "}
                              {selectedStudent.phone || selectedStudent.mobile_no || "N/A"}
                            </MDTypography>
                          </Grid>
                        </Grid>
                      </Box>
                      <Card sx={{ mt: 2, boxShadow: 2 }}>
                        <Box sx={{ bgcolor: "#1976d2", color: "#fff", p: 1.5 }}>
                          <MDTypography
                            variant="h6"
                            sx={{ fontWeight: 700, color: "#fff !important" }}
                          >
                            Fee Details - Select fee heads to pay
                          </MDTypography>
                        </Box>
                        <Box sx={{ width: "100%", bgcolor: "#fff" }}>
                          <DataGrid
                            autoHeight
                            rows={feeDetailRows}
                            columns={feeDetailColumns}
                            disableRowSelectionOnClick
                            pageSizeOptions={[5, 10, 25]}
                            initialState={{
                              pagination: { paginationModel: { pageSize: 5, page: 0 } },
                            }}
                            getRowHeight={() => 80}
                            localeText={{ noRowsLabel: "No pending fees found for this student" }}
                            sx={neutralGridSx}
                          />
                        </Box>

                        {/* 🎯 NEW PAYMENT MODAL (Step 3) */}
                        <Dialog
                          open={payModalOpen}
                          onClose={() => setPayModalOpen(false)}
                          maxWidth="sm"
                          fullWidth
                          PaperProps={{
                            sx: {
                              borderRadius: 3,
                            },
                          }}
                        >
                          <Box sx={{ p: 3 }}>
                            <MDTypography variant="h5" gutterBottom>
                              Collect Fee Payment
                            </MDTypography>
                            <MDTypography variant="body2" color="textSecondary" mb={3}>
                              {selectedFeeRow?.feeBatchName || "Fee Item"}
                            </MDTypography>

                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  required
                                  fullWidth
                                  size="medium"
                                  type="number"
                                  label="Fee Amount"
                                  value={paymentForm.fee_amount}
                                  disabled={
                                    selectedFeeRow?.feeTypeName?.toUpperCase() === "EXAMFEE"
                                  }
                                  inputProps={{
                                    min: 0.01,
                                    max: Number(selectedFeeRow?.feeDueRaw || 0),
                                    step: 0.01,
                                  }}
                                  onChange={(e) =>
                                    setPaymentForm({
                                      ...paymentForm,
                                      fee_amount: e.target.value,
                                    })
                                  }
                                  helperText={`Maximum due: ₹${formatDisplayAmount(
                                    selectedFeeRow?.feeDueRaw || 0
                                  )}`}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      fontSize: "18px",
                                      fontWeight: 600,
                                    },
                                    "& .MuiInputLabel-root": {
                                      fontSize: "16px",
                                      fontWeight: 600,
                                    },
                                    "& .MuiFormHelperText-root": {
                                      fontSize: "14px",
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  required
                                  fullWidth
                                  select
                                  label="Payment Mode"
                                  value={paymentForm.payment_mode}
                                  onChange={(e) =>
                                    setPaymentForm({
                                      ...paymentForm,
                                      payment_mode: e.target.value,
                                    })
                                  }
                                  size="medium"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  SelectProps={{
                                    native: true,
                                  }}
                                >
                                  <option value=""></option>
                                  <option value="cash">Cash</option>
                                  <option value="online">Online</option>
                                  <option value="dd">DD</option>
                                  <option value="cheque">Cheque</option>
                                </TextField>
                              </Grid>
                            </Grid>

                            <Box
                              sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}
                            >
                              <Button
                                variant="contained"
                                onClick={() => setPayModalOpen(false)}
                                sx={{
                                  minWidth: 90,
                                  height: 36,
                                  bgcolor: "#1976d2",
                                  color: "#fff",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  "&:hover": { bgcolor: "#115293" },
                                  boxShadow: "none",
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleSingleRowPay}
                                disabled={
                                  processingReceipt ||
                                  !selectedFeeRow ||
                                  !paymentForm.payment_mode ||
                                  !Number(paymentForm.fee_amount) ||
                                  Number(paymentForm.fee_amount) <= 0 ||
                                  Number(paymentForm.fee_amount) >
                                    Number(selectedFeeRow?.feeDueRaw || 0)
                                }
                                startIcon={
                                  processingReceipt ? (
                                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                                  ) : null
                                }
                                sx={{
                                  minWidth: 90,
                                  height: 36,
                                  bgcolor: "#1976d2",
                                  color: "#fff",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  "&:hover": {
                                    bgcolor: "#115293",
                                  },
                                  "&.Mui-disabled": {
                                    bgcolor: "#90caf9",
                                    color: "#fff",
                                  },
                                  boxShadow: "none",
                                }}
                              >
                                {processingReceipt ? receiptStatus || "Processing..." : "Pay Now"}
                              </Button>
                            </Box>
                          </Box>
                        </Dialog>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                            bgcolor: "#f8f9fa",
                            borderTop: "2px solid #1976d2",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, color: "#1976d2" }}>
                              Receipt Copies:
                            </MDTypography>
                            <TextField
                              select
                              value={noOfCopies}
                              onChange={(e) => setNoOfCopies(e.target.value)}
                              size="medium"
                              sx={{ width: 80, "& .MuiOutlinedInput-root": { bgcolor: "#fff" } }}
                            >
                              {[1, 2, 3, 4, 5].map((num) => (
                                <MenuItem key={num} value={num}>
                                  {num}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <span style={{ color: "#000", fontWeight: 600, marginRight: 8 }}>
                                Detailed Receipt
                              </span>
                              <Button
                                variant="outlined"
                                color="info"
                                size="medium"
                                sx={{
                                  minWidth: 140,
                                  borderColor: "#000",
                                  color: "#000",
                                  fontWeight: 700,
                                }}
                                onClick={handlePrintDetailedReceipt}
                                disabled={feeDetailRows.length === 0 || !selectedStudent}
                              >
                                Print
                              </Button>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 2,
                              py: 1,
                              bgcolor: "#fff3e0",
                              borderRadius: 1,
                              border: "2px solid #ff9800",
                            }}
                          >
                            <MDTypography variant="h6" sx={{ fontWeight: 700, color: "#e65100" }}>
                              Total Pending:
                            </MDTypography>
                            <MDTypography variant="h5" sx={{ fontWeight: 700, color: "#d84315" }}>
                              ₹ {formatDisplayAmount(calculateTotalPending())}
                            </MDTypography>
                          </Box>
                        </Box>
                      </Card>
                    </>
                  )}
                </Box>

                {!selectedStudent && (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <MDTypography variant="h6" color="text">
                      Please search for a student to view and collect fees
                    </MDTypography>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </DashboardLayoutMin>
  );
}

FeeReceipt.propTypes = {
  user: PropTypes.object,
  onBack: PropTypes.func,
};

export default FeeReceipt;
